import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import RelatedGuides from '../../components/common/RelatedGuides';

const BestTimeToVisit = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const monthData = [
    {
      month: 'January',
      weather: '70-78°F, Dry, Clear',
      crowds: 'High',
      prices: 'High',
      goodFor: 'Volcano hikes, photography, festivals'
    },
    {
      month: 'February',
      weather: '72-80°F, Dry, Sunny',
      crowds: 'High',
      prices: 'High',
      goodFor: 'Perfect weather, outdoor activities'
    },
    {
      month: 'March',
      weather: '73-82°F, Dry, Warm',
      crowds: 'Very High',
      prices: 'Very High',
      goodFor: 'Semana Santa (late March/early April)'
    },
    {
      month: 'April',
      weather: '74-83°F, Transition',
      crowds: 'High (Easter), Low (late)',
      prices: 'Variable',
      goodFor: 'Semana Santa, shoulder season deals'
    },
    {
      month: 'May',
      weather: '73-82°F, Rainy starts',
      crowds: 'Low',
      prices: 'Low',
      goodFor: 'Budget travel, fewer tourists'
    },
    {
      month: 'June',
      weather: '72-80°F, Rainy',
      crowds: 'Low',
      prices: 'Low',
      goodFor: 'Green landscapes, budget stays'
    },
    {
      month: 'July',
      weather: '71-79°F, Rainy',
      crowds: 'Medium',
      prices: 'Medium',
      goodFor: 'Summer break, lush scenery'
    },
    {
      month: 'August',
      weather: '71-79°F, Rainy',
      crowds: 'Medium',
      prices: 'Medium',
      goodFor: 'Cultural events, green season'
    },
    {
      month: 'September',
      weather: '70-78°F, Very Rainy',
      crowds: 'Very Low',
      prices: 'Very Low',
      goodFor: 'Best deals, solitude'
    },
    {
      month: 'October',
      weather: '70-77°F, Rainy',
      crowds: 'Low',
      prices: 'Low',
      goodFor: 'Budget travel, Day of the Dead prep'
    },
    {
      month: 'November',
      weather: '69-76°F, Transition',
      crowds: 'Medium',
      prices: 'Medium',
      goodFor: 'Day of the Dead, shoulder season'
    },
    {
      month: 'December',
      weather: '68-75°F, Dry returns',
      crowds: 'High',
      prices: 'High',
      goodFor: 'Christmas markets, New Year'
    }
  ];

  const faqData = [
    {
      q: 'What is the best month to visit Antigua Guatemala?',
      a: 'January through March is the best time to visit Antigua Guatemala. You get dry weather, clear skies for volcano views, comfortable temperatures (70-82°F), and ideal conditions for hiking Acatenango or Pacaya. February is particularly excellent — post-holiday crowds ease slightly, but weather remains perfect. If you want to experience Semana Santa (Holy Week), late March or early April is spectacular but requires booking 6-12 months ahead.'
    },
    {
      q: 'When is the rainy season in Antigua Guatemala?',
      a: 'The rainy season runs from May through October, with September being the wettest month. However, "rainy season" doesn\'t mean all-day rain. Expect afternoon showers (usually 2-4 hours) with sunny mornings. June through August sees moderate rain, while September and October get heavier downpours. The rain keeps landscapes lush and green, and you\'ll find significantly lower prices and fewer tourists.'
    },
    {
      q: 'Is Antigua Guatemala affected by hurricane season?',
      a: 'Guatemala is rarely directly hit by hurricanes due to its Pacific and Caribbean coast geography. However, hurricane season (June-November) can bring increased rainfall and occasional tropical storms. Antigua, located inland at 5,000 feet elevation, is well-protected. The main impact is heavier rain in September-October. Unlike Caribbean destinations, hurricanes are not a major concern when planning your Antigua trip.'
    },
    {
      q: 'Can you visit Antigua Guatemala during rainy season?',
      a: 'Absolutely. Rainy season (May-October) is actually a great time to visit if you don\'t mind afternoon showers. Mornings are typically sunny and perfect for sightseeing. You\'ll enjoy 40-60% lower hotel rates, empty streets, lush green landscapes, and authentic local experiences. Pack a rain jacket, plan outdoor activities for mornings, and embrace the quieter, more affordable side of Antigua. Volcano hiking is still possible but check weather forecasts closely.'
    },
    {
      q: 'What is Semana Santa in Antigua Guatemala?',
      a: 'Semana Santa (Holy Week, the week before Easter) is Antigua\'s most spectacular cultural event. The city transforms with elaborate alfombras (sawdust carpets), massive religious processions, and hundreds of thousands of visitors. It\'s an unforgettable experience but requires serious planning: book hotels 6-12 months ahead, expect 3-5x normal prices, prepare for massive crowds, and plan to stay the entire week (Thursday-Sunday are peak days). If you want authentic culture over convenience, this is the time to visit.'
    },
    {
      q: 'Is November a good time to visit Antigua Guatemala?',
      a: 'November is an excellent shoulder season month. The rainy season is ending, crowds are moderate, and prices drop significantly compared to high season. You\'ll catch Day of the Dead celebrations (November 1-2) with colorful cemetery visits and traditional foods. Weather is transitional — expect some rain early in the month, but conditions improve by mid-November. It\'s ideal for budget-conscious travelers who want good weather without peak-season crowds.'
    },
    {
      q: 'What is the weather like in Antigua Guatemala in December?',
      a: 'December marks the return of dry season with pleasant temperatures (68-75°F). Early December is quieter and more affordable, while mid-to-late December sees crowds surge for Christmas and New Year. You\'ll experience festive decorations, Christmas markets, posadas (traditional celebrations), and clear volcano views. Book early for the last two weeks of December — prices jump and availability drops. Weather is perfect for all activities including volcano hiking.'
    },
    {
      q: 'When is the cheapest time to visit Antigua Guatemala?',
      a: 'September and October offer the lowest prices — expect 40-60% discounts on hotels and tours compared to high season. May, June, and early November are also budget-friendly. You\'ll trade perfect weather for significant savings, but mornings are usually sunny for sightseeing. Avoid Easter week (most expensive), Christmas/New Year (second most expensive), and January-March (high season pricing). Book rainy season trips for the best deals.'
    },
    {
      q: 'Is April a good time to visit Antigua Guatemala?',
      a: 'April is split between two extremes. If Easter falls in April, the first 1-2 weeks are peak Semana Santa chaos — incredible cultural experience but maximum crowds and prices. After Easter, April becomes a fantastic shoulder season month with great weather, emptying crowds, and dropping prices. Late April offers the best of both worlds: dry season weather without high season crowds. Check the Easter calendar before booking.'
    },
    {
      q: 'Can you hike Acatenango year-round?',
      a: 'Yes, but conditions vary dramatically. January-April offers the best visibility, driest trails, and clearest Fuego volcano views. May-October hikes are possible but expect muddy trails, cloud cover, and potential rain — you might not see Fuego eruptions. September-October are the riskiest months due to heavy rain. If volcano hiking is your priority, book during dry season (November-April). Always check weather forecasts 48 hours before your hike regardless of season.'
    },
    {
      q: 'What should I pack for Antigua Guatemala in different seasons?',
      a: 'Dry season (Nov-Apr): Layers for cool mornings (60s°F) and warm afternoons (75-82°F), sunscreen, hat, sunglasses, light jacket for evenings. Rainy season (May-Oct): Everything above plus rain jacket, waterproof bag, quick-dry clothes, umbrella. Year-round: Comfortable walking shoes, modest clothing for churches, daypack, reusable water bottle. For volcano hiking: Warm layers, gloves, headlamp, sturdy boots regardless of season.'
    },
    {
      q: 'Is there a bad time to visit Antigua Guatemala?',
      a: 'There\'s no truly "bad" time, but September-October can be challenging due to heavy afternoon rains and potential flooding. If you need guaranteed sunshine and perfect conditions, avoid these months. However, even the rainiest months offer sunny mornings and incredible deals. The only genuinely difficult time is Semana Santa if you haven\'t booked ahead — you\'ll struggle to find accommodation and face overwhelming crowds. Otherwise, every season has its advantages depending on your priorities.'
    },
    {
      q: 'When is the best time for photography in Antigua Guatemala?',
      a: 'January through March offers the best photography conditions: clear skies for volcano backdrops, vibrant bougainvillea blooms, golden hour light without haze, and minimal rain. December and November are also excellent. For dramatic cloud formations and lush green landscapes, try June-August mornings. Semana Santa (late March/early April) provides unique cultural photography opportunities with alfombras and processions. Avoid September-October when cloud cover and rain limit visibility.'
    },
    {
      q: 'What are the best months for fewer crowds in Antigua Guatemala?',
      a: 'May, June, September, and October see the fewest tourists. You\'ll have restaurants, ruins, and streets largely to yourself. Early November and late April are also quiet. Avoid January-March (high season), Semana Santa week (maximum crowds), Christmas/New Year (second busiest), and US summer holidays in July-August (moderate crowds). If you value solitude and authentic local interactions over perfect weather, visit during rainy season.'
    }
  ];

  const relatedGuides = [
    {
      title: 'Complete Antigua Travel Guide',
      description: 'Everything you need to know before visiting Antigua Guatemala',
      link: '/antigua-guatemala-travel-guide',
      image: 'https://public.readdy.ai/ai/img_res/6dcdc6c9af28fa7a988d54bc16074ecb.jpg'
    },
    {
      title: '3-Day Antigua Itinerary',
      description: 'Perfect long weekend plan for first-time visitors',
      link: '/antigua-itinerary-3-days',
      image: 'https://readdy.ai/api/search-image?query=antigua%20guatemala%20santa%20catalina%20arch%20with%20volcano%20view%20and%20cobblestone%20streets%20in%20golden%20hour%20light&width=400&height=300&seq=related2&orientation=landscape'
    },
    {
      title: 'Things to Do in Antigua',
      description: 'Top activities, attractions, and experiences',
      link: '/things-to-do-in-antigua-guatemala',
      image: 'https://readdy.ai/api/search-image?query=antigua%20guatemala%20central%20park%20with%20fountain%20colonial%20buildings%20and%20tourists%20exploring%20under%20sunny%20weather&width=400&height=300&seq=related3&orientation=landscape'
    },
    {
      title: 'Acatenango Volcano Hike',
      description: 'Complete guide to hiking Acatenango from Antigua',
      link: '/acatenango-hike-from-antigua',
      image: 'https://readdy.ai/api/search-image?query=acatenango%20volcano%20summit%20with%20hikers%20watching%20fuego%20volcano%20eruptions%20at%20sunset%20with%20dramatic%20clouds&width=400&height=300&seq=related4&orientation=landscape'
    },
    {
      title: 'Best Hotels in Antigua',
      description: 'Where to stay for every budget and travel style',
      link: '/best-hotels-in-antigua-guatemala',
      image: 'https://readdy.ai/api/search-image?query=luxury%20colonial%20hotel%20courtyard%20in%20antigua%20guatemala%20with%20fountain%20colorful%20flowers%20and%20traditional%20architecture&width=400&height=300&seq=related5&orientation=landscape'
    },
    {
      title: 'Is Antigua Safe?',
      description: 'Safety guide and practical tips for travelers',
      link: '/is-antigua-guatemala-safe',
      image: 'https://readdy.ai/api/search-image?query=safe%20antigua%20guatemala%20streets%20with%20tourists%20walking%20during%20daytime%20colorful%20buildings%20and%20welcoming%20atmosphere&width=400&height=300&seq=related6&orientation=landscape'
    }
  ];

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Travel Guide', path: '/antigua-guatemala-travel-guide' },
    { label: 'Best Time to Visit', path: '/best-time-to-visit-antigua-guatemala' }
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "Best Time to Visit Antigua Guatemala: Complete Weather Guide",
        "description": "Expert guide to choosing the best month to visit Antigua Guatemala based on weather, crowds, prices, and events. Includes month-by-month breakdown and Semana Santa tips.",
        "image": "https://readdy.ai/api/search-image?query=antigua%20guatemala%20panoramic%20view%20with%20three%20volcanoes%20colorful%20colonial%20architecture%20and%20clear%20blue%20sky%20perfect%20weather%20conditions&width=1200&height=630&seq=hero1&orientation=landscape",
        "author": {
          "@type": "Organization",
          "name": "Antigua Guatemala Travel Guide"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Antigua Guatemala Travel Guide",
          "logo": {
            "@type": "ImageObject",
            "url": "https://la-antigua-guatemala.com/logo.png"
          }
        },
        "datePublished": "2025-01-15",
        "dateModified": "2025-01-15",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://la-antigua-guatemala.com/best-time-to-visit-antigua-guatemala"
        },
        "inLanguage": "en-US"
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://la-antigua-guatemala.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Travel Guide",
            "item": "https://la-antigua-guatemala.com/antigua-guatemala-travel-guide"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Best Time to Visit",
            "item": "https://la-antigua-guatemala.com/best-time-to-visit-antigua-guatemala"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a
          }
        }))
      }
    ]
  };

  return (
    <>
      <SEO
        title="Best Time to Visit Antigua Guatemala: Weather Guide (2026)"
        description="Expert guide to the best month to visit Antigua Guatemala. Compare dry vs rainy season, Semana Santa timing, and month-by-month weather breakdown."
        canonical="https://la-antigua-guatemala.com/best-time-to-visit-antigua-guatemala"
        ogImage={{
          url: "https://readdy.ai/api/search-image?query=antigua%20guatemala%20panoramic%20view%20with%20three%20volcanoes%20colorful%20colonial%20architecture%20and%20clear%20blue%20sky%20perfect%20weather%20conditions&width=1200&height=630&seq=hero1&orientation=landscape",
          width: 1200,
          height: 630
        }}
        schema={jsonLd}
      />

      <Navbar darkText />

      <article className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="relative h-[500px] w-full overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=antigua%20guatemala%20panoramic%20view%20with%20three%20volcanoes%20colorful%20colonial%20architecture%20and%20clear%20blue%20sky%20perfect%20weather%20conditions&width=1920&height=800&seq=hero2&orientation=landscape"
            alt="Antigua Guatemala with volcanoes under perfect weather"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white px-6 max-w-4xl">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Best Time to Visit Antigua Guatemala
              </h1>
              <p className="text-xl md:text-2xl font-light">
                Expert weather guide to choosing the perfect month for your trip
              </p>
            </div>
          </div>
        </div>

        {/* Breadcrumbs */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <Breadcrumbs items={breadcrumbItems} />
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto px-6 py-16">
          
          {/* Quick Take Box */}
          <div className="bg-teal-50 border-l-4 border-teal-500 p-8 mb-12 rounded-r-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Take</h2>
            <div className="space-y-3 text-gray-800">
              <p><strong>Best Overall:</strong> January through March (dry season, perfect weather, clear volcano views)</p>
              <p><strong>Best Value:</strong> May, June, September, October (40-60% lower prices, fewer crowds)</p>
              <p><strong>Best Cultural Experience:</strong> Late March/Early April (Semana Santa — book 6-12 months ahead)</p>
              <p><strong>Best for Volcano Hiking:</strong> December through April (dry trails, clear visibility)</p>
              <p><strong>Avoid If You Need Perfect Weather:</strong> September-October (heaviest rain, though mornings are often sunny)</p>
            </div>
          </div>

          {/* Quick Answer */}
          <div className="prose prose-lg max-w-none mb-16">
            <p className="text-xl text-gray-700 leading-relaxed">
              The <strong>best time to visit Antigua Guatemala is January through March</strong> — the heart of dry season when you get consistently sunny days, comfortable temperatures (70-82°F), clear volcano views, and ideal conditions for hiking Acatenango or Pacaya. February is particularly excellent: post-holiday crowds ease slightly while weather remains perfect.
            </p>
            <p className="text-xl text-gray-700 leading-relaxed">
              That said, Antigua is a year-round destination. If you prioritize budget and solitude over perfect weather, the rainy season (May-October) offers 40-60% lower prices, lush green landscapes, and authentic local experiences. Rain typically falls in afternoon bursts, leaving mornings sunny for sightseeing. The key is matching your visit to your priorities: weather, crowds, budget, or cultural events like <Link to="/antigua-guatemala-travel-guide" className="text-teal-600 hover:text-teal-700 font-semibold whitespace-nowrap">Semana Santa</Link>.
            </p>
          </div>

          {/* Weather Overview */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Understanding Antigua's Weather</h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Antigua sits at 5,000 feet elevation in Guatemala's central highlands, giving it a spring-like climate year-round. Unlike coastal destinations with extreme heat or humidity, Antigua maintains comfortable temperatures with two distinct seasons: dry (November-April) and rainy (May-October).
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              The elevation means cool mornings (often in the 60s°F) and warm afternoons (70s-low 80s°F). Nights can be chilly, especially December-February. You'll never experience extreme heat, but you'll also never need heavy winter gear. Pack layers and you're set for any month.
            </p>

            {/* Dry vs Rainy Season Comparison */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Factor</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Dry Season (Nov-Apr)</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Rainy Season (May-Oct)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900">Weather</td>
                      <td className="px-6 py-4 text-gray-700">Sunny, clear skies, minimal rain</td>
                      <td className="px-6 py-4 text-gray-700">Sunny mornings, afternoon showers (2-4 hrs)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Temperature</td>
                      <td className="px-6 py-4 text-gray-700">68-83°F (cooler Dec-Jan)</td>
                      <td className="px-6 py-4 text-gray-700">70-82°F (consistent)</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900">Crowds</td>
                      <td className="px-6 py-4 text-gray-700">High (peak: Jan-Mar, Easter, Dec)</td>
                      <td className="px-6 py-4 text-gray-700">Low to moderate</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Prices</td>
                      <td className="px-6 py-4 text-gray-700">High (2-3x rainy season rates)</td>
                      <td className="px-6 py-4 text-gray-700">Low (40-60% discounts)</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900">Volcano Views</td>
                      <td className="px-6 py-4 text-gray-700">Excellent visibility</td>
                      <td className="px-6 py-4 text-gray-700">Often cloudy afternoons</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Landscapes</td>
                      <td className="px-6 py-4 text-gray-700">Dry, brown hillsides</td>
                      <td className="px-6 py-4 text-gray-700">Lush, vibrant green</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900">Best For</td>
                      <td className="px-6 py-4 text-gray-700">First-timers, volcano hikes, photography</td>
                      <td className="px-6 py-4 text-gray-700">Budget travelers, repeat visitors, solitude</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Pro Tip 1 */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-12 rounded-r-lg">
            <div className="flex items-start">
              <i className="ri-lightbulb-line text-2xl text-amber-600 mr-4 mt-1"></i>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Pro Tip: Rainy Season Isn't Scary</h3>
                <p className="text-gray-800">
                  "Rainy season" sounds worse than it is. Rain typically falls 2-4pm daily, leaving mornings perfect for sightseeing. You'll have Antigua largely to yourself, pay half-price for hotels, and see landscapes at their greenest. Pack a rain jacket and embrace it.
                </p>
              </div>
            </div>
          </div>

          {/* Month-by-Month Breakdown */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Month-by-Month Weather Guide</h2>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Here's what to expect every month in Antigua Guatemala, including weather patterns, crowd levels, pricing trends, and what each month is best suited for.
            </p>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-12">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Month</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Weather</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Crowds</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Prices</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Good For</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {monthData.map((month, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 font-semibold text-gray-900">{month.month}</td>
                        <td className="px-6 py-4 text-gray-700">{month.weather}</td>
                        <td className="px-6 py-4 text-gray-700">{month.crowds}</td>
                        <td className="px-6 py-4 text-gray-700">{month.prices}</td>
                        <td className="px-6 py-4 text-gray-700">{month.goodFor}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Detailed Monthly Descriptions */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">January: Peak Dry Season</h3>
                <p className="text-gray-700 leading-relaxed">
                  January delivers consistently excellent weather with clear skies, comfortable temperatures (70-78°F), and perfect volcano visibility. It's one of the busiest months as travelers escape northern winters. Expect high hotel rates but guaranteed sunshine. Ideal for <Link to="/acatenango-hike-from-antigua" className="text-teal-600 hover:text-teal-700 font-semibold whitespace-nowrap">Acatenango hikes</Link> and photography. Book accommodations 2-3 months ahead.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">February: Sweet Spot</h3>
                <p className="text-gray-700 leading-relaxed">
                  February is arguably the best month to visit Antigua Guatemala. Weather is perfect (72-80°F), crowds ease slightly after January, and you still get dry season reliability. Valentine's Day brings romantic travelers, but overall it's less hectic than surrounding months. Excellent for all outdoor activities. This is my personal favorite month.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">March: Building to Semana Santa</h3>
                <p className="text-gray-700 leading-relaxed">
                  March starts beautifully with warm, dry weather (73-82°F). If Easter falls in late March, the last week transforms into Semana Santa chaos — spectacular cultural experience but maximum crowds and prices. Early March is still relatively calm. If you want to experience Holy Week, book 6-12 months ahead. If you want to avoid it, check the Easter calendar before planning.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">April: Post-Easter Shoulder Season</h3>
                <p className="text-gray-700 leading-relaxed">
                  April is split personality month. If Easter falls in April, the first 1-2 weeks are peak season. After Easter, Antigua empties dramatically while weather remains excellent (74-83°F). Late April is a fantastic time to visit — dry season conditions, dropping prices, and far fewer tourists. Rain begins appearing late in the month, signaling the transition to rainy season.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">May: Rainy Season Begins</h3>
                <p className="text-gray-700 leading-relaxed">
                  May marks the start of rainy season, but don't let that scare you. Mornings are typically sunny and perfect for sightseeing, with afternoon showers starting around 2-3pm. Temperatures remain comfortable (73-82°F), crowds disappear, and hotel rates drop 40-50%. It's an excellent budget month if you can handle occasional rain. Landscapes turn lush and green.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">June: Green Season Value</h3>
                <p className="text-gray-700 leading-relaxed">
                  June continues rainy season patterns with reliable afternoon showers but sunny mornings. Temperatures stay pleasant (72-80°F), and you'll have Antigua largely to yourself. This is prime budget travel time with significant discounts on hotels and tours. The countryside is at its greenest. Pack a rain jacket and plan outdoor activities before 2pm. Great for repeat visitors who've already seen Antigua in dry season.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">July: Summer Break Uptick</h3>
                <p className="text-gray-700 leading-relaxed">
                  July sees a moderate increase in visitors as US families travel during summer break. Weather remains rainy (71-79°F) with afternoon showers, but crowds are still far below dry season levels. Prices rise slightly from May-June lows but remain reasonable. It's a solid middle-ground month — not the cheapest, not the busiest, not the driest, but perfectly pleasant if you embrace the rain. Mornings are usually clear for volcano views and sightseeing. Good for travelers seeking balance between weather and value.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">August: Steady Rainy Season</h3>
                <p className="text-gray-700 leading-relaxed">
                  August continues July's patterns with consistent afternoon rain, moderate crowds, and mid-range pricing. Temperatures stay comfortable (71-79°F). It's a solid middle-ground month — not the cheapest, not the busiest, not the driest, but perfectly pleasant if you embrace the rain. Mornings are usually clear for volcano views and sightseeing. Good for travelers seeking balance between weather and value.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">September: Wettest Month</h3>
                <p className="text-gray-700 leading-relaxed">
                  September is the rainiest month in Antigua Guatemala with heavy afternoon downpours and occasional all-day rain. Temperatures remain mild (70-78°F), but expect limited volcano visibility and muddy trails. However, this is also the cheapest month with rock-bottom hotel rates and near-empty streets. If you're flexible with weather and prioritize budget and solitude, September offers incredible value. Not recommended for first-time visitors or volcano hiking.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">October: Tail End of Rainy Season</h3>
                <p className="text-gray-700 leading-relaxed">
                  October continues heavy rain patterns but starts improving toward month's end. Temperatures stay pleasant (70-77°F), crowds remain low, and prices stay budget-friendly. Late October sees rain frequency decreasing as dry season approaches. Good for budget travelers willing to gamble on weather. Day of the Dead preparations begin, adding cultural interest.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">November: Shoulder Season Gem</h3>
                <p className="text-gray-700 leading-relaxed">
                  November is an excellent shoulder season month. Rainy season winds down (especially mid-to-late month), crowds stay moderate, and prices remain reasonable. You'll catch Day of the Dead celebrations (November 1-2) with colorful cemetery visits and traditional foods. Weather improves throughout the month (69-76°F) as dry season approaches. Great value month with improving conditions. Book for late November for best weather.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">December: Dry Season Returns</h3>
                <p className="text-gray-700 leading-relaxed">
                  December marks the return of dry season with pleasant temperatures (68-75°F) and decreasing rain. Early December is quieter and more affordable, while mid-to-late December sees crowds surge for Christmas and New Year. You'll experience festive decorations, Christmas markets, posadas (traditional celebrations), and clear volcano views. Book early for the last two weeks — prices jump and availability drops. Weather is perfect for all activities.
                </p>
              </div>
            </div>
          </section>

          {/* Pro Tip 2 */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-12 rounded-r-lg">
            <div className="flex items-start">
              <i className="ri-lightbulb-line text-2xl text-amber-600 mr-4 mt-1"></i>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Pro Tip: Shoulder Season Strategy</h3>
                <p className="text-gray-800">
                  Late April and November offer the best of both worlds: good weather, moderate crowds, and reasonable prices. You'll avoid peak season chaos without sacrificing too much sunshine. These are the smartest months for value-conscious travelers who still want reliable weather.
                </p>
              </div>
            </div>
          </div>

          {/* Best Time For... */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Best Time for Specific Interests</h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-sun-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Best Weather</h3>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  <strong>January through March</strong> delivers the most consistent sunshine, clear skies, and comfortable temperatures. February is the sweet spot with perfect conditions and slightly fewer crowds than January or March.
                </p>
                <p className="text-sm text-gray-600">
                  Expect: 70-82°F, minimal rain, excellent volcano visibility, ideal hiking conditions
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-user-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Fewer Crowds</h3>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  <strong>May, June, September, and October</strong> see the fewest tourists. You'll have restaurants, ruins, and streets largely to yourself. Early November and late April are also quiet.
                </p>
                <p className="text-sm text-gray-600">
                  Trade-off: Afternoon rain, but mornings are usually sunny for sightseeing
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Budget Travel</h3>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  <strong>September and October</strong> offer the lowest prices — expect 40-60% discounts on hotels and tours. May, June, and early November are also budget-friendly.
                </p>
                <p className="text-sm text-gray-600">
                  Best value: Late April (post-Easter) and November (improving weather, low prices)
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-mountain-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Volcano Hiking</h3>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  <strong>December through April</strong> provides the best conditions for Acatenango and Pacaya hikes: dry trails, clear visibility, and reliable Fuego volcano views. January-March is peak hiking season.
                </p>
                <p className="text-sm text-gray-600">
                  Avoid: September-October (muddy trails, cloud cover, limited visibility)
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-camera-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Photography</h3>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  <strong>January through March</strong> offers the best photography conditions: clear skies for volcano backdrops, vibrant bougainvillea blooms, golden hour light without haze, and minimal rain.
                </p>
                <p className="text-sm text-gray-600">
                  Alternative: June-August mornings for dramatic clouds and lush green landscapes
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-calendar-event-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Festivals</h3>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  <strong>Late March/Early April</strong> for Semana Santa (Holy Week) — Guatemala's most spectacular cultural event. Also consider November 1-2 for Day of the Dead and December for Christmas markets.
                </p>
                <p className="text-sm text-gray-600">
                  Critical: Book Semana Santa accommodations 6-12 months ahead
                </p>
              </div>
            </div>
          </section>

          {/* Pro Tip 3 */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-12 rounded-r-lg">
            <div className="flex items-start">
              <i className="ri-lightbulb-line text-2xl text-amber-600 mr-4 mt-1"></i>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Pro Tip: Volcano Hiking in Rainy Season</h3>
                <p className="text-gray-800">
                  You can hike Acatenango during rainy season (May-October), but it's a gamble. Trails are muddy, clouds often obscure Fuego volcano, and rain can hit anytime. If volcano hiking is your main goal, stick to dry season (November-April). If you're flexible, book a rainy season hike and hope for luck — you might score empty trails and dramatic clouds.
                </p>
              </div>
            </div>
          </div>

          {/* Semana Santa Section */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Semana Santa in Antigua Guatemala</h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Semana Santa (Holy Week, the week before Easter) is Antigua's most spectacular and chaotic event. The city transforms into a living museum of colonial religious tradition with elaborate alfombras (sawdust carpets), massive processions, and hundreds of thousands of visitors.
            </p>

            <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What to Expect During Semana Santa</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Alfombras (Sawdust Carpets)</p>
                    <p className="text-gray-700">Intricate, colorful carpets made from dyed sawdust, flowers, and pine needles cover streets. Families spend hours creating them, only to have processions walk over them minutes later. It's breathtaking art.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Religious Processions</p>
                    <p className="text-gray-700">Massive processions carry religious floats (andas) through streets, accompanied by purple-robed cucuruchos. Thursday and Friday are the most intense days with processions lasting 12+ hours.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Massive Crowds</p>
                    <p className="text-gray-700">Antigua's population swells from 45,000 to 200,000+. Streets are packed, restaurants have long waits, and you'll need patience. It's overwhelming but unforgettable.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Sky-High Prices</p>
                    <p className="text-gray-700">Hotels charge 3-5x normal rates with minimum 3-7 night stays. Budget hotels that normally cost $30/night jump to $100-150. Luxury hotels can hit $500+/night. Tours and restaurants also increase prices.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Limited Availability</p>
                    <p className="text-gray-700">Hotels sell out 6-12 months in advance. If you're booking less than 3 months out, expect very limited options and inflated prices. Many hotels require full week bookings.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8 rounded-r-lg">
              <h3 className="font-bold text-gray-900 mb-3">Semana Santa Booking Timeline</h3>
              <ul className="space-y-2 text-gray-800">
                <li><strong>12 months ahead:</strong> Best hotel selection, reasonable prices</li>
                <li><strong>6 months ahead:</strong> Good options still available, prices rising</li>
                <li><strong>3 months ahead:</strong> Limited availability, high prices</li>
                <li><strong>1 month ahead:</strong> Very few options, expect to pay premium rates or stay outside Antigua</li>
                <li><strong>Last minute:</strong> Nearly impossible to find accommodation in Antigua proper</li>
              </ul>
            </div>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              <strong>Should you visit during Semana Santa?</strong> If you want to experience Guatemala's most important cultural event and don't mind crowds, chaos, and high prices — absolutely. It's a once-in-a-lifetime experience. If you prefer peaceful sightseeing and budget travel, avoid this week entirely. There's no middle ground.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed">
              <strong>Peak days:</strong> Thursday (Jueves Santo) and Friday (Viernes Santo) are the most intense. Saturday is calmer, and Sunday (Easter) sees crowds departing. If you can only visit part of the week, Thursday-Saturday captures the essence.
            </p>
          </section>

          {/* Pro Tip 4 */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-12 rounded-r-lg">
            <div className="flex items-start">
              <i className="ri-lightbulb-line text-2xl text-amber-600 mr-4 mt-1"></i>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Pro Tip: Semana Santa Alternatives</h3>
                <p className="text-gray-800">
                  If Semana Santa hotels are sold out or too expensive, consider staying in nearby towns (San Miguel Dueñas, Ciudad Vieja, Jocotenango) and taking tuk-tuks into Antigua for processions. You'll save money and escape the chaos at night. Book these backup towns early too — they fill up fast.
                </p>
              </div>
            </div>
          </div>

          {/* Hurricane Season */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Hurricane Season in Guatemala</h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Guatemala's official hurricane season runs June through November, coinciding with rainy season. However, unlike Caribbean destinations, Guatemala is rarely directly hit by hurricanes due to its Pacific and Caribbean coast geography and mountainous interior.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Antigua, located inland at 5,000 feet elevation, is well-protected from hurricane impacts. The main effect of hurricane season is increased rainfall, particularly in September and October when tropical storms can bring heavier-than-normal downpours.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8 rounded-r-lg">
              <h3 className="font-bold text-gray-900 mb-3">What Hurricane Season Actually Means for Antigua</h3>
              <ul className="space-y-2 text-gray-800">
                <li><strong>Increased rain:</strong> Heavier afternoon showers, occasional all-day rain</li>
                <li><strong>Tropical storms:</strong> Rare but possible, bringing 1-3 days of steady rain</li>
                <li><strong>Flooding:</strong> Some streets may flood temporarily during heavy rain</li>
                <li><strong> Landslides:</strong> Possible on mountain roads during extreme weather</li>
                <li><strong>Direct hurricane hits:</strong> Extremely rare in Antigua's location</li>
              </ul>
            </div>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              <strong>Bottom line:</strong> Hurricane season shouldn't deter you from visiting Antigua. The risk of serious disruption is low. The bigger concern is simply the increased rain that comes with rainy season. Monitor weather forecasts, pack rain gear, and you'll be fine. This isn't Cancun or the Caribbean where hurricanes are a legitimate planning concern.
            </p>
          </section>

          {/* What to Pack */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">What to Pack by Season</h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Dry Season (Nov-Apr)</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Layers for cool mornings (60s°F) and warm afternoons (75-82°F)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Light jacket or sweater for evenings</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Sunscreen (SPF 30+), hat, sunglasses</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Comfortable walking shoes (cobblestones)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Modest clothing for churches (covered shoulders/knees)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Daypack for day trips</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Reusable water bottle</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Rainy Season (May-Oct)</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Everything from dry season list, plus:</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Rain jacket or poncho (lightweight, packable)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Waterproof bag or dry sack for electronics</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Quick-dry clothes (avoid cotton)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Compact umbrella</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Waterproof shoes or sandals</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                    <span>Plastic bags for wet clothes</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Volcano Hiking (Year-Round)</h3>
              <p className="text-gray-700 mb-4">
                If you're hiking Acatenango or Pacaya, add these essentials regardless of season:
              </p>
              <ul className="grid md:grid-cols-2 gap-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                  <span>Warm layers (summit temps: 30-40°F)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                  <span>Gloves and warm hat</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                  <span>Headlamp with extra batteries</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                  <span>Sturdy hiking boots</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                  <span>Trekking poles (helpful for descent)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                  <span>High-energy snacks</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                  <span>2-3 liters of water</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                  <span>Sleeping bag (if camping overnight)</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Pro Tip 5 */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-12 rounded-r-lg">
            <div className="flex items-start">
              <i className="ri-lightbulb-line text-2xl text-amber-600 mr-4 mt-1"></i>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Pro Tip: Layer Like a Local</h3>
                <p className="text-gray-800">
                  Antigua's elevation means significant temperature swings. Start your day in a light jacket (60s°F), strip down to a t-shirt by noon (80°F), then add layers again for evening (60s°F). Locals wear layers year-round. Pack versatile pieces you can add or remove throughout the day.
                </p>
              </div>
            </div>
          </div>

          {/* Common Mistakes */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Common Mistakes When Choosing Travel Dates</h2>
            
            <div className="space-y-6">
              <div className="bg-white border-l-4 border-red-500 p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Booking Semana Santa Last Minute</h3>
                <p className="text-gray-700">
                  Hotels sell out 6-12 months ahead for Holy Week. If you're booking less than 3 months out, expect very limited options, inflated prices, or no availability. Either book a year ahead or avoid this week entirely.
                </p>
              </div>

              <div className="bg-white border-l-4 border-red-500 p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Avoiding Rainy Season Completely</h3>
                <p className="text-gray-700">
                  Many travelers skip May-October thinking it's a washout. Reality: mornings are usually sunny, rain falls predictably in afternoons, and you'll save 40-60% on costs while avoiding crowds. If you're flexible with weather, rainy season is excellent value.
                </p>
              </div>

              <div className="bg-white border-l-4 border-red-500 p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Planning Volcano Hikes in September-October</h3>
                <p className="text-gray-700">
                  These are the wettest months with muddy trails, cloud cover, and limited visibility. If Acatenango or Pacaya is your priority, book during dry season (November-April). Don't gamble on the rainiest months for your once-in-a-lifetime volcano hike.
                </p>
              </div>

              <div className="bg-white border-l-4 border-red-500 p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Underpacking for Cool Mornings/Evenings</h3>
                <p className="text-gray-700">
                  Antigua's elevation means cool mornings and evenings year-round (often 60s°F). Many travelers pack only for warm weather and end up cold. Bring layers, a light jacket, and long pants even in "summer."
                </p>
              </div>

              <div className="bg-white border-l-4 border-red-500 p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Expecting Caribbean Beach Weather</h3>
                <p className="text-gray-700">
                  Antigua is in the highlands at 5,000 feet, not on the coast. It's spring-like year-round, not tropical beach weather. Don't expect 90°F heat or swimming weather. Temperatures stay comfortable (70s-low 80s°F) but never get truly hot.
                </p>
              </div>

              <div className="bg-white border-l-4 border-red-500 p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Not Checking Easter Dates</h3>
                <p className="text-gray-700">
                  Easter moves every year (late March or April). If you're trying to avoid Semana Santa crowds, check the Easter calendar before booking. Late March one year might be calm, but the next year it could be peak chaos.
                </p>
              </div>

              <div className="bg-white border-l-4 border-red-500 p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Assuming Hurricane Season = Danger</h3>
                <p className="text-gray-700">
                  Guatemala is rarely hit by hurricanes due to its geography. Hurricane season (June-November) mainly means increased rain, not storm evacuations. Antigua is inland and well-protected. Don't let "hurricane season" scare you away from good deals.
                </p>
              </div>
            </div>
          </section>

          {/* Pro Tip 6 */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-12 rounded-r-lg">
            <div className="flex items-start">
              <i className="ri-lightbulb-line text-2xl text-amber-600 mr-4 mt-1"></i>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Pro Tip: Book Dry Season Early</h3>
                <p className="text-gray-800">
                  January-March hotels fill up fast, especially mid-range and budget options. Book 2-3 months ahead for best selection and prices. Luxury hotels have more availability but still book early for preferred dates. Procrastinate and you'll pay premium rates or settle for less desirable locations.
                </p>
              </div>
            </div>
          </div>

          {/* Is There a Bad Time? */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Is There a Bad Time to Visit Antigua Guatemala?</h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Honestly? Not really. Antigua is a year-round destination where every season has advantages depending on your priorities.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              <strong>September-October</strong> come closest to "challenging" due to heavy rain, limited volcano visibility, and muddy trails. If you need guaranteed sunshine and perfect conditions, these months aren't ideal. However, even the rainiest months offer sunny mornings, incredible deals, and near-empty streets. For budget travelers who embrace rain, they're actually excellent.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              The only genuinely difficult time is <strong>Semana Santa if you haven't booked ahead</strong>. Showing up during Holy Week without reservations means you'll struggle to find accommodation, face overwhelming crowds, and pay inflated prices. Either plan a year ahead or avoid this week entirely.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Otherwise, there's no "bad" time — just different experiences:
            </p>
            <ul className="space-y-3 text-lg text-gray-700 mb-6">
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 text-2xl mr-2"></i>
                <span><strong>Want perfect weather?</strong> Visit January-March</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 text-2xl mr-2"></i>
                <span><strong>Want to save money?</strong> Visit May-October</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 text-2xl mr-2"></i>
                <span><strong>Want cultural immersion?</strong> Visit during Semana Santa (book early)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 text-2xl mr-2"></i>
                <span><strong>Want fewer crowds?</strong> Visit May, June, September, October</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 text-2xl mr-2"></i>
                <span><strong>Want balance?</strong> Visit late April or November</span>
              </li>
            </ul>
            <p className="text-lg text-gray-700 leading-relaxed">
              Match your visit to your priorities, pack appropriately, and you'll have a great time regardless of when you go.
            </p>
          </section>

          {/* Final Recommendations */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Final Recommendations by Traveler Type</h2>
            
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">First-Time Visitors</h3>
                <p className="text-gray-700 mb-3">
                  <strong>Best months:</strong> January, February, March, December
                </p>
                <p className="text-gray-700">
                  You want reliable weather, clear volcano views, and ideal conditions for all activities. Dry season guarantees the best first impression of Antigua. Book 2-3 months ahead for good hotel selection.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Budget Travelers</h3>
                <p className="text-gray-700 mb-3">
                  <strong>Best months:</strong> May, June, September, October, early November
                </p>
                <p className="text-gray-700">
                  Save 40-60% on hotels and tours by visiting during rainy season. Mornings are usually sunny for sightseeing. Pack rain gear, embrace afternoon showers, and enjoy empty streets and authentic local experiences.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Volcano Hikers</h3>
                <p className="text-gray-700 mb-3">
                  <strong>Best months:</strong> December, January, February, March, April
                </p>
                <p className="text-gray-700">
                  Dry trails, clear visibility, and reliable Fuego volcano views make dry season essential for Acatenango or Pacaya hikes. January-March is peak hiking season. Book tours 1-2 weeks ahead during high season.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Photographers</h3>
                <p className="text-gray-700 mb-3">
                  <strong>Best months:</strong> January, February, March
                </p>
                <p className="text-gray-700">
                  Clear skies for volcano backdrops, vibrant bougainvillea blooms, golden hour light without haze, and minimal rain. December and November are also excellent. For dramatic clouds and lush landscapes, try June-August mornings.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Culture Seekers</h3>
                <p className="text-gray-700 mb-3">
                  <strong>Best months:</strong> Late March/Early April (Semana Santa), November 1-2 (Day of the Dead), December (Christmas)
                </p>
                <p className="text-gray-700">
                  Experience Guatemala's most important cultural events. Semana Santa requires booking 6-12 months ahead and tolerance for massive crowds. Day of the Dead and Christmas are more manageable but still special.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Repeat Visitors</h3>
                <p className="text-gray-700 mb-3">
                  <strong>Best months:</strong> May, June, July, August, November
                </p>
                <p className="text-gray-700">
                  You've seen Antigua in dry season. Now experience the green season with lush landscapes, fewer tourists, and authentic local life. Rainy season offers a completely different perspective on the city you already love.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Families with School-Age Kids</h3>
                <p className="text-gray-700 mb-3">
                  <strong>Best months:</strong> December, July, August
                </p>
                <p className="text-gray-700">
                  Limited to school breaks. December offers perfect weather but book early for Christmas/New Year. July-August means rainy season but manageable with morning activities. Avoid Semana Santa unless kids are older and can handle crowds.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Digital Nomads / Long-Term Visitors</h3>
                <p className="text-gray-700 mb-3">
                  <strong>Best months:</strong> May through November
                </p>
                <p className="text-gray-700">
                  Avoid high season crowds and prices. Rainy season offers affordable long-term rentals, quieter cafes for working, and authentic local experiences. You'll have time to explore on sunny mornings and work during afternoon rain.
                </p>
              </div>
            </div>
          </section>

          {/* Mid-Page CTA */}
          <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-12 text-center mb-16 shadow-lg">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Plan Your Antigua Trip?
            </h2>
            <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
              Let us create a personalized itinerary based on your travel dates, interests, and budget
            </p>
            <Link
              to="/plan-my-antigua-trip"
              className="inline-block bg-white text-gray-900 px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap"
            >
              Plan My Trip
            </Link>
          </div>

          {/* Final CTA */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-12 text-center shadow-lg">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Get Your Free Antigua Itinerary PDF
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Download our comprehensive guide with day-by-day plans, budget breakdowns, and insider tips
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/plan-my-antigua-trip"
                className="inline-block bg-white text-gray-900 px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap"
              >
                Plan My Trip
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </>
  );
};

export default BestTimeToVisit;